// Client-side wiring for the logged-out profile's desktop exit-intent signup modal
// (Social::Profile::ExitIntentSignupComponent renders the hidden markup, always present).
//
// Behavior contract: exit intent is desktop only (mouse-leave toward the top of the viewport),
// shown at most once per session, dismissible via backdrop/X.
//
// Click tracking rides the generic [data-click-track] binder in profile.wjs.jsx — the
// CTA is in the DOM from the server render, so no extra tracking wiring here.

const HIDDEN_CLASS = 'u-displayNone';
const EXIT_SHOWN_KEY = 'profileAutotestExitIntentShown';

const sessionFlag = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return true; // no sessionStorage (privacy mode) → behave as already shown
  }
};

const setSessionFlag = (key: string): void => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // ignore — worst case the prompt stays suppressed
  }
};

const setUpExitIntent = (): void => {
  const found = document.querySelector<HTMLElement>('.js-autotest-exit-intent');
  if (!found || sessionFlag(EXIT_SHOWN_KEY)) return;
  const modal = found; // narrowed non-null; the hoisted handlers below close over this

  // WCAG AA: the dialog is keyboard-operable — Escape closes it, focus moves into it on
  // open and is restored on close, and Tab is trapped inside while it's open.
  let lastFocused: HTMLElement | null = null;

  const focusable = (): HTMLElement[] =>
    Array.from(
      modal.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'),
    ).filter((el) => el.offsetParent !== null);

  function closeModal(): void {
    modal.classList.add(HIDDEN_CLASS);
    document.removeEventListener('keydown', onKeydown);
    lastFocused?.focus();
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      closeModal();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusable();
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const openModal = (): void => {
    setSessionFlag(EXIT_SHOWN_KEY);
    lastFocused = document.activeElement as HTMLElement | null;
    modal.classList.remove(HIDDEN_CLASS);
    document.addEventListener('keydown', onKeydown);
    focusable()[0]?.focus();
  };

  const onMouseOut = (event: MouseEvent): void => {
    // Fire only when the pointer leaves through the top of the viewport (toward the
    // URL bar / tabs) — the conventional exit-intent heuristic, desktop-only by nature.
    if (event.relatedTarget || event.clientY > 0) return;

    document.removeEventListener('mouseout', onMouseOut);
    openModal();
  };

  modal.querySelectorAll('.js-autotest-exit-intent-dismiss').forEach((el) => {
    el.addEventListener('click', () => closeModal());
  });

  document.addEventListener('mouseout', onMouseOut);
};

export const setUpProfileExitIntentSignup = (): void => {
  setUpExitIntent();
};
